import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="6175" className="hidden lg:flex items-center gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-20 bg-[#d6d1bb]/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="hidden lg:flex items-center gap-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="relative">
          <Link to={item.href || "#"} className="group relative inline-block py-2 text-[15px] font-bold uppercase tracking-wider text-black transition-colors hover:text-[#35bdfe]">
            {item.label}
            <span className="absolute bottom-0 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-[#35bdfe] transition-all duration-300 ease-out group-hover:w-full" />
          </Link>
        </div>)}
    </nav>;
}
function MobileNav({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-6">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-6 w-32 bg-[#d6d1bb]/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-6">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-[#d6d1bb]/40 pb-4 last:border-0">
          <Link to={item.href || "#"} className="block text-lg font-bold uppercase tracking-wide text-black hover:text-[#35bdfe] transition-colors" onClick={onClose}>
            {item.label}
          </Link>
        </div>)}
      <div className="pt-4">
        <Button className="w-full rounded-full bg-[#35bdfe] text-white hover:bg-[#3bd9e7] shadow-sm transition-all duration-300" size="lg" asChild>
          <Link to="/ContactPage">Work with Us</Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b", isScrolled ? "bg-[#fafbf1]/80 backdrop-blur-xl border-[#d6d1bb]/40 shadow-sm" : "bg-[#fafbf1] border-transparent")}>
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="flex items-center gap-2">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1608537/img/renewpath_logo.png" alt="NB Socials" className="h-7 w-auto object-contain" />
          </Link>
        </div>

        {}
        <MenuProvider menu_id="16" className="hidden lg:block">
          <DesktopNav />
        </MenuProvider>

        {}
        <div className="hidden lg:flex items-center">
          <Button className="rounded-full px-8 bg-[#35bdfe] text-white hover:bg-[#3bd9e7] shadow-sm hover:shadow-md transition-all duration-300 font-bold uppercase tracking-wide" asChild>
            <Link to="/ContactPage">Work with Us</Link>
          </Button>
        </div>

        {}
        <div className="flex lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" className="text-black">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="absolute top-20 left-0 w-full bg-[#fafbf1]/95 backdrop-blur-xl border-b border-[#d6d1bb]/40 shadow-lg lg:hidden animate-in slide-in-from-top-5 duration-200">
          <MenuProvider menu_id="16">
            <MobileNav onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}